import { Modifier, SelectionState } from 'draft-js';
import {
  ENTITY_TYPE,
  ENTITY_MUTABILITY,
} from '@src/constants/pronunciationCheck';

const normalizeNswResponse = (rawResponse) => {
  if (rawResponse?.status !== 1) {
    return { items: [], text: null };
  }
  const nswRequest = rawResponse.result?.nswRequest;
  const nswList = nswRequest?.nsw || [];
  const items = nswList.map((item, index) => ({
    id: item.id || `nsw_${index}`,
    word: item.orig,
    pronunciation: item.pronunciation,
    type: item.type,
    startIndex: item.position.start,
    endIndex: item.position.end,
  }));
  return { items, text: nswRequest?.text ?? null };
};

const filterContainedItems = (nswItems) => {
  const sorted = [...nswItems].sort((a, b) => a.startIndex - b.startIndex);
  const result = [];

  for (let i = 0; i < sorted.length; i += 1) {
    const item = sorted[i];
    const isContainedByAny = result.some(
      (parent) =>
        item.startIndex >= parent.startIndex &&
        item.endIndex <= parent.endIndex,
    );
    if (!isContainedByAny) {
      result.push(item);
    }
  }

  return result;
};

const applyHighlightEntities = (contentState, nswItems) => {
  let updatedContentState = contentState;
  const blocks = contentState.getBlocksAsArray();
  let globalOffset = 0;

  blocks.forEach((block) => {
    const blockKey = block.getKey();
    const blockLength = block.getText().length;

    nswItems.forEach(
      ({ id, word, pronunciation, type, startIndex, endIndex }) => {
        if (
          startIndex >= globalOffset &&
          endIndex <= globalOffset + blockLength
        ) {
          const localStart = startIndex - globalOffset;
          const localEnd = endIndex - globalOffset;
          updatedContentState = updatedContentState.createEntity(
            ENTITY_TYPE.HIGHLIGHT,
            ENTITY_MUTABILITY.IMMUTABLE,
            { id, word, pronunciation, type },
          );
          const entityKey = updatedContentState.getLastCreatedEntityKey();
          const selection = SelectionState.createEmpty(blockKey).merge({
            anchorOffset: localStart,
            focusOffset: localEnd,
          });
          updatedContentState = Modifier.applyEntity(
            updatedContentState,
            selection,
            entityKey,
          );
        }
      },
    );

    globalOffset += blockLength + 1;
  });

  return updatedContentState;
};

const removeEntitiesByPredicate = (contentState, shouldRemove) => {
  let updatedContentState = contentState;

  contentState.getBlockMap().forEach((block) => {
    const blockKey = block.getKey();
    block.findEntityRanges(
      (character) => {
        const entityKey = character.getEntity();
        if (!entityKey) return false;
        const type = contentState.getEntity(entityKey).getType();
        return (
          type === ENTITY_TYPE.HIGHLIGHT || type === ENTITY_TYPE.FIXED_WORD
        );
      },
      (start, end) => {
        const entityKey = block.getEntityAt(start);
        const data = contentState.getEntity(entityKey).getData();
        if (shouldRemove(data)) {
          const selection = SelectionState.createEmpty(blockKey).merge({
            anchorOffset: start,
            focusOffset: end,
          });
          updatedContentState = Modifier.applyEntity(
            updatedContentState,
            selection,
            null,
          );
        }
      },
    );
  });

  return updatedContentState;
};

const removeAllEntities = (contentState) =>
  removeEntitiesByPredicate(contentState, () => true);

const isMatchingNswEntity = (character, contentState, word) => {
  const entityKey = character.getEntity();
  if (!entityKey) return false;
  const entity = contentState.getEntity(entityKey);
  const type = entity.getType();
  if (type !== ENTITY_TYPE.HIGHLIGHT && type !== ENTITY_TYPE.FIXED_WORD) {
    return false;
  }
  const data = entity.getData();
  return data.word === word;
};

/**
 * Đánh dấu 1 từ đã áp dụng: đổi entity HIGHLIGHT → FIXED_WORD.
 * Không thay đổi text trong editor (giữ nguyên nội dung, tránh tăng credit).
 */
const markWordAsFixed = (contentState, entityKey, pronunciation) => {
  let updatedContentState = contentState;
  let targetSelection = null;
  let entityData = null;

  contentState.getBlockMap().forEach((block) => {
    if (targetSelection) return;
    const blockKey = block.getKey();
    block.findEntityRanges(
      (character) => character.getEntity() === entityKey,
      (start, end) => {
        if (!targetSelection) {
          targetSelection = SelectionState.createEmpty(blockKey).merge({
            anchorOffset: start,
            focusOffset: end,
          });
          entityData = contentState.getEntity(entityKey).getData();
        }
      },
    );
  });

  if (!targetSelection) return contentState;

  const originalPronunciation =
    entityData.originalPronunciation || entityData.pronunciation;
  const appliedPronunciation = pronunciation || originalPronunciation;
  const pronunciationChanged = appliedPronunciation !== originalPronunciation;

  const fixedData = {
    ...entityData,
    pronunciation: appliedPronunciation,
    originalPronunciation,
    pronunciationChanged,
  };

  updatedContentState = updatedContentState.createEntity(
    ENTITY_TYPE.FIXED_WORD,
    ENTITY_MUTABILITY.IMMUTABLE,
    fixedData,
  );
  const fixedEntityKey = updatedContentState.getLastCreatedEntityKey();

  updatedContentState = Modifier.applyEntity(
    updatedContentState,
    targetSelection,
    fixedEntityKey,
  );

  return updatedContentState;
};

/**
 * Đánh dấu TẤT CẢ từ khớp word/pronunciation: HIGHLIGHT → FIXED_WORD.
 * Không thay đổi text trong editor.
 */
const markAllWordsAsFixed = (contentState, word, pronunciation) => {
  let updatedContentState = contentState;

  contentState.getBlockMap().forEach((block) => {
    const blockKey = block.getKey();
    block.findEntityRanges(
      (character) => isMatchingNswEntity(character, contentState, word),
      (start, end) => {
        const selection = SelectionState.createEmpty(blockKey).merge({
          anchorOffset: start,
          focusOffset: end,
        });

        const originalEntityKey = block.getEntityAt(start);
        const originalData = contentState
          .getEntity(originalEntityKey)
          .getData();

        const originalPronunciation =
          originalData.originalPronunciation || originalData.pronunciation;
        const appliedPronunciation = pronunciation || originalPronunciation;
        const pronunciationChanged =
          appliedPronunciation !== originalPronunciation;

        const fixedData = {
          ...originalData,
          pronunciation: appliedPronunciation,
          originalPronunciation,
          pronunciationChanged,
        };

        updatedContentState = updatedContentState.createEntity(
          ENTITY_TYPE.FIXED_WORD,
          ENTITY_MUTABILITY.IMMUTABLE,
          fixedData,
        );
        const fixedEntityKey = updatedContentState.getLastCreatedEntityKey();

        updatedContentState = Modifier.applyEntity(
          updatedContentState,
          selection,
          fixedEntityKey,
        );
      },
    );
  });

  return updatedContentState;
};

const getEntityKeysByType = (contentState, entityType) => {
  const seen = new Set();
  const entityKeys = [];
  contentState.getBlockMap().forEach((block) => {
    block.findEntityRanges(
      (character) => {
        const entityKey = character.getEntity();
        if (!entityKey) return false;
        const entity = contentState.getEntity(entityKey);
        return entity.getType() === entityType;
      },
      (start) => {
        const entityKey = block.getEntityAt(start);
        if (!seen.has(entityKey)) {
          seen.add(entityKey);
          entityKeys.push(entityKey);
        }
      },
    );
  });
  return entityKeys;
};

const getHighlightEntityKeys = (contentState) =>
  getEntityKeysByType(contentState, ENTITY_TYPE.HIGHLIGHT);

const getFixedWordEntityKeys = (contentState) =>
  getEntityKeysByType(contentState, ENTITY_TYPE.FIXED_WORD);

/**
 * Remove entities that have no matching Redux item.
 * Used when undo restores entities whose items were already removed.
 */
const removeExtraEntities = (contentState, survivingItems) => {
  const survivingIds = new Set(survivingItems.map((item) => item.id));
  return removeEntitiesByPredicate(
    contentState,
    (data) => !survivingIds.has(data.id),
  );
};

/**
 * Extract all NSW items (both applied and unapplied) with real-time positions
 * from editor content. Positions are recalculated from entity locations
 * instead of using stale Redux values.
 *
 * When selectionState is provided, only entities fully within the selection
 * are included and positions are adjusted relative to the selection start.
 */
const getNswPayloadFromContent = (contentState, selectionState) => {
  const result = [];
  const seen = new Set();
  const blocks = contentState.getBlocksAsArray();

  let hasSelection = false;
  let selStart = 0;
  let selEnd = 0;

  if (selectionState) {
    const startKey = selectionState.getStartKey();
    const endKey = selectionState.getEndKey();
    const startOffset = selectionState.getStartOffset();
    const endOffset = selectionState.getEndOffset();

    let offset = 0;
    blocks.forEach((block) => {
      const key = block.getKey();
      if (key === startKey) selStart = offset + startOffset;
      if (key === endKey) selEnd = offset + endOffset;
      offset += block.getText().length + 1;
    });

    hasSelection = selStart !== selEnd;
  }

  let globalOffset = 0;

  blocks.forEach((block) => {
    block.findEntityRanges(
      (character) => {
        const entityKey = character.getEntity();
        if (!entityKey) return false;
        const type = contentState.getEntity(entityKey).getType();
        return type === ENTITY_TYPE.FIXED_WORD;
      },
      (start, end) => {
        const entityKey = block.getEntityAt(start);
        if (seen.has(entityKey)) return;
        seen.add(entityKey);

        const globalStart = globalOffset + start;
        const globalEnd = globalOffset + end;

        if (hasSelection && (globalStart < selStart || globalEnd > selEnd)) {
          return;
        }

        const data = contentState.getEntity(entityKey).getData();
        if (data.pronunciationChanged === false) return;

        const positionOffset = hasSelection ? selStart : 0;
        result.push({
          orig: data.word,
          pronunciation: data.pronunciation,
          position: {
            start: globalStart - positionOffset,
            end: globalEnd - positionOffset,
          },
          type: data.type,
        });
      },
    );

    globalOffset += block.getText().length + 1;
  });

  return result;
};

// TODO: Công thức tính credit cho tính năng kiểm tra cách đọc, hiện tạm thời để 0 để không hiển thị cost
const calculatePronunciationCheckCredits = (creditsCost) => creditsCost || 0;

export {
  normalizeNswResponse,
  filterContainedItems,
  applyHighlightEntities,
  removeAllEntities,
  isMatchingNswEntity,
  markWordAsFixed,
  markAllWordsAsFixed,
  getHighlightEntityKeys,
  getFixedWordEntityKeys,
  removeExtraEntities,
  getNswPayloadFromContent,
  calculatePronunciationCheckCredits,
};
